/**
 * Permission catalog — the granular "who-can-do-what" vocabulary (ADR-0030).
 *
 * This is the second authorization layer, distinct from module entitlements
 * (`modules.ts`). They answer different questions and compose:
 *
 *   - **Entitlement** (`company_entitlements.modules`, company-level/billing):
 *     *has this tenant bought the Production module?* — `entitledProcedureFor`.
 *   - **Permission** (`roles.permissions`, per-person/org):
 *     *may THIS member record a QC lot?* — `permissionGate` (ADR-0030).
 *
 * A garment procedure ends up gated by both: `entitledProcedureFor("garment.qc")`
 * **and** `permissionGate("garment.qc.record")`. RLS stays the floor underneath.
 *
 * Permission strings are `resource.action`. Roles (`roles.permissions text[]`)
 * store an explicit list of these strings; the Owner role carries the single
 * wildcard `*` which grants everything. The catalog lives here — shared by the
 * api (gate + role CRUD validation) and web (role editor) — so it is never
 * duplicated, exactly like the module registry.
 *
 * @see modules.ts — the sibling entitlement registry
 * @see docs/adr/0030-custom-roles-permissions-and-org.md
 */
import { z } from "zod";

/**
 * Permissions grouped by area — the grouping drives the role-editor UI and
 * keeps the catalog auditable. The flat list (`PERMISSIONS`) is derived from
 * these groups, so adding a permission is a one-line edit here.
 */
export const PERMISSION_GROUPS = {
  sales: [
    "invoices.view",
    "invoices.create",
    "invoices.issue",
    "invoices.send",
    "invoices.void",
    "creditNotes.view",
    "creditNotes.raise",
    "receipts.view",
    "receipts.record",
    "receipts.void",
    "customers.view",
    "customers.manage",
    "collections.view",
  ],
  pos: [
    "pos.ring",
    "pos.shift.manage",
    "pos.void",
    "pos.refund",
    "pos.discount.override",
  ],
  purchases: [
    "vendors.view",
    "vendors.manage",
    "purchaseOrders.view",
    "purchaseOrders.manage",
    "goodsReceipts.view",
    "goodsReceipts.manage",
    "bills.view",
    "bills.create",
    "bills.approve",
    "items.view",
    "items.manage",
  ],
  inventory: [
    "inventory.view",
    "inventory.manage",
    "inventory.transfer",
    "inventory.adjust",
  ],
  money: [
    "vendorPayments.view",
    "vendorPayments.record",
    "bankAccounts.view",
    "bankAccounts.manage",
    "pettyCash.view",
    "pettyCash.record",
    "transfers.record",
    "reconcile.view",
    "reconcile.manage",
  ],
  compliance: [
    "books.view",
    "books.manage",
    "gstReturns.view",
    "tds.view",
    "itc04.view",
    "reports.view",
    "reports.export",
    "audit.view",
  ],
  garment: [
    "garment.styles.view",
    "garment.styles.edit",
    "garment.costing.view",
    "garment.costing.lock",
    "garment.samples.view",
    "garment.samples.manage",
    "garment.samples.approve",
    "garment.tna.view",
    "garment.tna.manage",
    "garment.materials.view",
    "garment.materials.manage",
    "garment.materials.issue",
    "garment.production.view",
    "garment.production.record",
    "garment.qc.view",
    "garment.qc.record",
    "garment.dispatch.view",
    "garment.dispatch.confirm",
    "garment.orderBook.view",
  ],
  // Store admin (Commerce). `publish` is split from `manage` because it is
  // what puts a product in front of buyers — the same reason
  // `garment.samples.approve` is its own permission. Gated by the
  // `commerce.admin` entitlement as well; the two compose.
  // `orders.approve` is split from `orders.manage` for the same reason as
  // `catalog.publish`: approving is the consequential act — it is what commits
  // the business to fulfilling, and (once the hand-off lands) what mints a
  // proforma. Reading the order book is not the same right.
  // `quotes.send` is split from `quotes.manage` on the same principle: drafting
  // a line sheet is internal work, sending it puts a price in front of a buyer.
  store: [
    "store.catalog.view",
    "store.catalog.manage",
    "store.catalog.publish",
    "store.orders.view",
    "store.orders.manage",
    "store.orders.approve",
    "store.quotes.view",
    "store.quotes.manage",
    "store.quotes.send",
  ],
  people: ["employees.view", "employees.manage", "payroll.view", "payroll.run"],
  admin: ["team.manage", "roles.manage", "settings.manage", "billing.manage", "approvals.manage"],
} as const;

/** Area keys — the role editor renders one section per area. */
export type PermissionArea = keyof typeof PERMISSION_GROUPS;
export const PERMISSION_AREAS = Object.keys(PERMISSION_GROUPS) as readonly PermissionArea[];

/** The flat, canonical list of every grantable permission string. */
export const PERMISSIONS = Object.values(PERMISSION_GROUPS).flat() as readonly string[];

/** A single permission string, e.g. `"garment.qc.record"`. */
export type Permission = (typeof PERMISSION_GROUPS)[PermissionArea][number];

/** The Owner wildcard — grants every permission, present and future. */
export const ALL_PERMISSIONS = "*" as const;

/**
 * Zod validator for a permission string on the wire (role-CRUD inputs). Accepts
 * any known permission or the `*` wildcard; unknown strings are rejected so a
 * typo can't silently create a dead grant. Built from the flat list.
 */
export const permissionSchema = z.enum([
  ALL_PERMISSIONS,
  ...(PERMISSIONS as [string, ...string[]]),
] as [string, ...string[]]);

/**
 * Pure authorization predicate — does this granted set satisfy `needed`?
 * The single source of the gate's decision (mirrors `isModuleEntitled`), so the
 * `*` wildcard semantics live in exactly one place and are unit-tested in
 * isolation. `permissionGate` and every UI guard call through here.
 */
export function hasPermission(granted: readonly string[], needed: Permission): boolean {
  return granted.includes(ALL_PERMISSIONS) || granted.includes(needed);
}

/** All permission strings under an area prefix — bundle-authoring helper. */
function permsLike(prefix: string): readonly string[] {
  return PERMISSIONS.filter((p) => p.startsWith(prefix));
}

/** A seedable default role — a named, reusable bundle of permissions. */
export interface SystemRole {
  /** Stable slug — the upsert key for `seedSystemRoles`; never shown raw. */
  readonly key: string;
  /** Display name shown in the team UI. */
  readonly name: string;
  /** One-line description of what the role is for. */
  readonly description: string;
  /** Suggested home department (a `DEFAULT_DEPARTMENTS` name) for the seed. */
  readonly department: string | null;
  /** The granted permission strings (or `["*"]` for Owner). */
  readonly permissions: readonly string[];
}

/**
 * Default departments seeded for a new workspace — the org skeleton a garment
 * manufacturer recognises (modelled on a real factory org chart). Sub-functions
 * of Operations carry `parent: "Operations"`; the seed resolves the parent FK.
 */
export const DEFAULT_DEPARTMENTS: ReadonlyArray<{ name: string; parent: string | null }> = [
  { name: "Management", parent: null },
  { name: "Marketing", parent: null },
  { name: "Sales", parent: null },
  { name: "Finance", parent: null },
  { name: "HR / Admin", parent: null },
  { name: "Operations", parent: null },
  { name: "R&D / NPD", parent: "Operations" },
  { name: "Production", parent: "Operations" },
  { name: "Quality", parent: "Operations" },
  { name: "Warehouse", parent: "Operations" },
  { name: "Logistics", parent: "Operations" },
];

/**
 * The default role set seeded per workspace — a reusable bundle each member is
 * assigned to (multiple people share one role). Modelled on a garment
 * manufacturer's org so the factory's functions map 1:1. `is_system` roles are
 * read-only in the UI; companies clone-to-customise or build their own.
 */
export const SYSTEM_ROLES: readonly SystemRole[] = [
  {
    key: "owner",
    name: "Owner",
    description: "Full access including billing, team, and every approval. Maps from the legacy Founder role.",
    department: "Management",
    permissions: [ALL_PERMISSIONS],
  },
  {
    key: "admin",
    name: "Admin / HR",
    description: "Runs the team, people, and settings. No money movement.",
    department: "HR / Admin",
    permissions: [
      "team.manage",
      "roles.manage",
      "settings.manage",
      "approvals.manage",
      ...permsLike("employees."),
      ...permsLike("payroll."),
      "reports.view",
      "reports.export",
      "audit.view",
    ],
  },
  {
    key: "accountant",
    name: "Accountant",
    description: "Full finance: invoicing, bills, payments, banking, GST/TDS, and the books.",
    department: "Finance",
    permissions: [
      ...permsLike("invoices."),
      ...permsLike("creditNotes."),
      ...permsLike("receipts."),
      ...permsLike("bills."),
      ...permsLike("vendorPayments."),
      ...permsLike("bankAccounts."),
      ...permsLike("pettyCash."),
      "transfers.record",
      ...permsLike("reconcile."),
      ...permsLike("books."),
      "gstReturns.view",
      "tds.view",
      "itc04.view",
      "reports.view",
      "reports.export",
      "customers.view",
      "vendors.view",
      "items.view",
      "inventory.view",
      "store.catalog.view",
      "store.orders.view",
      "store.quotes.view",
    ],
  },
  {
    key: "sales_manager",
    name: "Sales Manager",
    description: "Quotes, invoices, customers, and collections.",
    department: "Sales",
    permissions: [
      "invoices.view",
      "invoices.create",
      "invoices.issue",
      "invoices.send",
      "creditNotes.view",
      "receipts.view",
      "receipts.record",
      "customers.view",
      "customers.manage",
      "collections.view",
      "items.view",
      "reports.view",
      ...permsLike("store.catalog."),
      ...permsLike("store.orders."),
      // `permsLike` matches on prefix, so each new store module needs its own
      // line here — a new `store.quotes.*` permission is not picked up by the
      // two above.
      ...permsLike("store.quotes."),
      // The store product form records opening stock, and its warehouse
      // picker reads `warehouses.list` — which is gated on inventory.view.
      "inventory.view",
    ],
  },
  {
    key: "store_staff",
    name: "Store Sales Staff",
    description: "Counter sales — raise an invoice and record the receipt.",
    department: "Sales",
    permissions: [
      "invoices.view",
      "invoices.create",
      "receipts.view",
      "receipts.record",
      "customers.view",
      "items.view",
    ],
  },
  {
    key: "cashier",
    name: "Cashier",
    description: "Rings POS counter sales and runs their register. No voids, refunds, or large discounts.",
    department: "Sales",
    permissions: [
      "pos.ring",
      "pos.shift.manage",
      "items.view",
      "customers.view",
      "receipts.view",
      "receipts.record",
      "invoices.view",
    ],
  },
  {
    key: "marketing",
    name: "Marketing",
    description: "Catalogue, customers, and reports — read-mostly.",
    department: "Marketing",
    permissions: ["customers.view", "items.view", "reports.view"],
  },
  {
    key: "merchandiser",
    name: "Merchandiser",
    description: "Owns the Style File end-to-end: order, materials, samples, T&A, costing review.",
    department: "Operations",
    permissions: [
      ...permsLike("garment.styles."),
      ...permsLike("garment.materials."),
      "garment.samples.view",
      "garment.samples.manage",
      "garment.samples.approve",
      "garment.tna.view",
      "garment.tna.manage",
      "garment.costing.view",
      "garment.orderBook.view",
      ...permsLike("purchaseOrders."),
      "goodsReceipts.view",
      "vendors.view",
      "items.view",
    ],
  },
  {
    key: "designer",
    name: "Designer / R&D",
    description: "Design & development and the sampling rounds.",
    department: "R&D / NPD",
    permissions: ["garment.styles.view", "garment.styles.edit", "garment.samples.view", "garment.samples.manage", "garment.samples.approve", "garment.orderBook.view"],
  },
  {
    key: "production_head",
    name: "Production Head",
    description: "Runs cutting & assembly output, issues materials, drives the T&A plan.",
    department: "Production",
    permissions: [
      ...permsLike("garment.production."),
      "garment.qc.view",
      "garment.materials.view",
      "garment.materials.issue",
      "garment.tna.view",
      "garment.tna.manage",
      "garment.styles.view",
      "garment.samples.view",
      "garment.samples.approve",
      "garment.orderBook.view",
    ],
  },
  {
    key: "cutting_master",
    name: "Cutting Master",
    description: "Records production output and issues cut materials.",
    department: "Production",
    permissions: ["garment.production.record", "garment.production.view", "garment.materials.issue", "garment.styles.view", "garment.samples.view", "garment.samples.approve"],
  },
  {
    key: "qc_inspector",
    name: "QC Inspector",
    description: "End-line AQL inspection and the dispatch gate.",
    department: "Quality",
    permissions: ["garment.qc.view", "garment.qc.record", "garment.styles.view", "garment.samples.view", "garment.samples.approve"],
  },
  {
    key: "warehouse",
    name: "Warehouse / Inventory",
    description: "Goods receipt, inventory, and material stock.",
    department: "Warehouse",
    permissions: [
      "goodsReceipts.view",
      "goodsReceipts.manage",
      "items.view",
      "garment.materials.view",
      "purchaseOrders.view",
      "inventory.view",
      "inventory.manage",
      "inventory.adjust",
      "inventory.transfer",
    ],
  },
  {
    key: "dispatch",
    name: "Dispatch / Logistics",
    description: "Packs, ships, and confirms dispatch (which drafts the export invoice).",
    department: "Logistics",
    permissions: ["garment.dispatch.view", "garment.dispatch.confirm", "garment.styles.view", "garment.orderBook.view"],
  },
  {
    key: "floor_worker",
    name: "Floor Worker",
    description: "Shop-floor staff (helpers, cutting, finishing). No app login in V1; mobile Floor Mode lands in V2.",
    department: "Production",
    permissions: [],
  },
];

/** Lookup a seeded role bundle by key. */
export function systemRole(key: string): SystemRole | undefined {
  return SYSTEM_ROLES.find((r) => r.key === key);
}

/**
 * Backward-compat bridge (ADR-0030 §migration). Memberships created before the
 * roles table have no `role_id` — the auth middleware resolves their permissions
 * from the legacy `memberships.role` enum via this map, so the gate behaves
 * identically the day it's switched on. New memberships carry a `role_id` and
 * skip this entirely.
 *
 *   - `Founder`  → Owner wildcard.
 *   - `Operator` → the broad transactional generalist (no billing/team/settings,
 *     no founder-only money actions — matches the legacy capability matrix).
 *   - `Employee` → no app access (WhatsApp-only, system-decisions §2).
 */
export const LEGACY_ROLE_PERMISSIONS: Record<"Founder" | "Operator" | "Employee", readonly string[]> = {
  Founder: [ALL_PERMISSIONS],
  Operator: [
    "invoices.view",
    "invoices.create",
    "invoices.issue",
    "invoices.send",
    "receipts.view",
    "receipts.record",
    "customers.view",
    "customers.manage",
    "collections.view",
    "items.view",
    "items.manage",
    "vendors.view",
    "vendors.manage",
    "purchaseOrders.view",
    "purchaseOrders.manage",
    "goodsReceipts.view",
    "goodsReceipts.manage",
    "bills.view",
    "bills.create",
    "vendorPayments.view",
    "vendorPayments.record",
    "bankAccounts.view",
    "pettyCash.view",
    "pettyCash.record",
    "transfers.record",
    "reconcile.view",
    "books.view",
    "gstReturns.view",
    "tds.view",
    "itc04.view",
    "reports.view",
    "reports.export",
    "employees.view",
    "payroll.view",
    "garment.styles.view",
    "garment.styles.edit",
    "garment.costing.view",
    "garment.samples.view",
    "garment.samples.manage",
    "garment.samples.approve",
    "garment.tna.view",
    "garment.tna.manage",
    "garment.materials.view",
    "garment.materials.manage",
    "garment.materials.issue",
    "garment.production.view",
    "garment.production.record",
    "garment.qc.view",
    "garment.qc.record",
    "garment.dispatch.view",
    "garment.dispatch.confirm",
    "garment.orderBook.view",
    "inventory.view",
    "inventory.adjust",
    // Store admin. Granted in full because the legacy Operator is the broad
    // transactional generalist — it already issues invoices, which is more
    // consequential than publishing a product. Withholding these here would
    // silently lock existing role_id-less memberships out of a module they
    // can reach today.
    "store.catalog.view",
    "store.catalog.manage",
    "store.catalog.publish",
  ],
  Employee: [],
};

/**
 * Resolve a legacy `memberships.role` to its permission set. Used by the auth
 * middleware for memberships without a `role_id`. Unknown/`null` → no access.
 */
export function legacyRolePermissions(role: string | null): readonly string[] {
  if (role === "Founder" || role === "Operator" || role === "Employee") {
    return LEGACY_ROLE_PERMISSIONS[role];
  }
  return [];
}
